/**
 * 67.61 (2), ground litter (the owner, Oct 19: *"The other question is how to cheaply add ground litter and stuff across
 * the map"*): grass tufts, leaves, needles, twigs, pebbles and debris over all the ground near the camera, by the biome and
 * the ground's material — draw only. Unlike the ground scatter (groundScatter.ts: around the dressed site and the trees),
 * the litter is anchored to the camera and covers any map:
 *   - the ground is cut into cubes `LITTER.cellPeople` across (world space, so any surface — a planet, a flat sheet —
 *     falls through them); each cube's pieces come from a hash of its key: **deterministic from position**, no state, no
 *     sim, nothing to hit, the same pieces every time the camera comes back;
 *   - only the cubes the ground crosses within `reachPeople` of the camera's ground point hold pieces: **a fixed pool**
 *     of instance slots (one block per cube per kind) recycled as the camera moves, at most `cellsPerFrame` cubes made a
 *     frame — **a fixed per-frame cost at any map size**;
 *   - one InstancedMesh per kind (six draws), a few triangles a piece, tinted per instance; each piece **fades** (shrinks
 *     to nothing) over the reach's last third in the vertex shader;
 *   - none where the ground says `blocked` (structures, a dressed site's keep-out; roads when there are roads).
 * The ground is reached through `LitterGround` (the drawn ground's point along up, its biome and material), so the sheet's
 * flat bands and a planet's drawn ground are the same field. Presentation only.
 *
 * Not everywhere (the owner, Oct 19: *"But I don't think I want litter everywhere all the time, just a higher likelihood
 * that a region is higher density though some could be sparse / bare if it makes sense with whole biome / weather
 * system"*): `litterDensity` is a regional field — value noise `LITTER.regionPeople` across, keyed on the planet's seed —
 * whose odds come from the biome, the ground's material and the planet's climate (its weather mix's wetness, dryness and
 * wind): most regions sparse to medium, some dense, some bare (ice, salt, the barrens and the burn, an arid or windswept
 * region); the jungle and broadleaf floors denser. The Lab's **Ground litter** dial scales it all (0 = off).
 */
import * as THREE from 'three';
import type { GroundMaterial, Vec3 } from '../types';
import { markShared } from './meshBake';

export type LitterKind = 'blades' | 'leaves' | 'needles' | 'twigs' | 'pebbles' | 'debris';
export const LITTER_KINDS: LitterKind[] = ['blades', 'leaves', 'needles', 'twigs', 'pebbles', 'debris'];
/** What the litter reads of a place: the forest's biome there (null: open ground; 'jungle' is the forest-ground sheet's floor, no stand in play names it since 447). */
export type LitterBiome = string | null;

/** The field's dials (people): its cubes, its reach, the fade's start (a share of the reach), the slots a cube holds per kind, the cubes made a frame. */
export const LITTER = {
  cellPeople: 3,
  reachPeople: 24,
  fadeFrom: 0.6,
  slots: { blades: 24, leaves: 16, needles: 10, twigs: 5, pebbles: 8, debris: 5 } as Record<LitterKind, number>,
  /** × each piece's size */
  pieceScale: 1.4,
  cellsPerFrame: 3,
  /** the regional density's noise, this many people across */
  regionPeople: 140,
  /** a cube is kept until it is this × the reach away (made within the reach): no flicker at the rim */
  keepFactor: 1.25,
  /**
   * 69.76 (the owner: "it still blips in when you move fast, and can sometimes blip out on a per instance basis"): the walk
   * is led along the focus's motion `leadS` ahead (at most the keep's margin past the reach, so what it makes is kept); at
   * speed more cubes a frame — `rateMargin` × the cubes the reach's front sweeps a frame, up to `maxCellsPerFrame` and
   * `budgetMs` past the first `cellsPerFrame`; and each cube made grows in over `growS` (its pieces' birth, in the shader),
   * so one made late is never a pop.
   */
  leadS: 1,
  rateMargin: 1.5,
  maxCellsPerFrame: 24,
  budgetMs: 1,
  growS: 0.35,
};

/** 67.61: the Lab's **Ground litter** dial — × the whole field (0: off). */
export const LITTER_SCALES = ['0', '0.5', '1', '1.5'] as const;
let scaleOverride: number | null = null;
/** The Lab's launch sets it (`null` returns to the page's `?litter=`, else 0: off). */
export function setLitterScale(v: number | null): void { scaleOverride = v; }
/**
 * 74.21: a throwaway of the litter's look for the shader warm-up (rendering/programWarm.ts) — the fading material's one program
 * (the jungle field's layers take the same), linked at the load rather than at the first cube drawn. The uniforms are the
 * throwaway's own: a program is keyed by its code, not its uniforms' objects.
 */
export function litterWarmPart(): THREE.Object3D {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 1, 0, 0, 0, 1, 0], 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 0, 0, 1], 3));
  g.setAttribute('aLitterBorn', new THREE.InstancedBufferAttribute(new Float32Array(1), 1));
  const m = new THREE.InstancedMesh(g, fadingMaterial({ value: new THREE.Vector3() }, { value: new THREE.Vector2(1, 2) }), 1);
  m.name = 'warm:litter'; m.setMatrixAt(0, new THREE.Matrix4()); m.setColorAt(0, new THREE.Color(1, 1, 1));
  return m;
}

export function litterScale(): number {
  if (scaleOverride !== null) return scaleOverride;
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('litter') : null;
    const v = q === null ? NaN : Number(q);
    return Number.isFinite(v) ? Math.max(0, Math.min(2, v)) : 0;
  } catch { return 0; }
}

/** A planet's climate as the litter reads it, from its weather mix (shares, 0 … 1): wet (rain, fog, storm), dry (dust, ash), windy (storm, dust). */
export interface LitterClimate { wet: number; dry: number; windy: number }
export function litterClimate(mix: Partial<Record<string, number>>): LitterClimate {
  const total = Object.values(mix).reduce<number>((a, v) => a + (v ?? 0), 0) || 1, w = (k: string) => (mix[k] ?? 0) / total;
  return { wet: w('rain') + w('fog') + w('storm'), dry: w('dust') + w('ash'), windy: w('storm') + w('dust') };
}
/** Where a biome's regions fall (shares of the noise): bare below `bare`, dense above `dense`, sparse to medium between. */
const BANDS: Record<string, { bare: number; dense: number }> = {
  jungle: { bare: 0.02, dense: 0.45 }, broadleaf: { bare: 0.06, dense: 0.55 }, birchGlade: { bare: 0.08, dense: 0.6 }, lanternGrove: { bare: 0.08, dense: 0.6 },
  firStand: { bare: 0.1, dense: 0.65 }, hollowWood: { bare: 0.1, dense: 0.62 }, heath: { bare: 0.25, dense: 0.82 }, scrub: { bare: 0.25, dense: 0.82 },
  savanna: { bare: 0.3, dense: 0.85 }, burn: { bare: 0.6, dense: 0.97 }, ashBarrens: { bare: 0.65, dense: 0.98 }, open: { bare: 0.22, dense: 0.8 },
};
const BARER: Partial<Record<GroundMaterial, number>> = { ice: 0.45, salt: 0.35, snow: 0.2, rock: 0.2, sand: 0.15, ash: 0.1 };
/** A smooth 3D value noise at `x` (a lattice of hashed corners, smoothstepped), 0 … 1; `seed` an integer. */
function valueNoise(x: number, y: number, z: number, seed: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z), fx = x - xi, fy = y - yi, fz = z - zi;
  const s = (t: number) => t * t * (3 - 2 * t), u = s(fx), v = s(fy), w = s(fz);
  const h = (i: number, j: number, k: number) => hash(xi + i, yi + j, zi + k, seed);
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  return lerp(lerp(lerp(h(0, 0, 0), h(1, 0, 0), u), lerp(h(0, 1, 0), h(1, 1, 0), u), v), lerp(lerp(h(0, 0, 1), h(1, 0, 1), u), lerp(h(0, 1, 1), h(1, 1, 1), u), v), w);
}
/**
 * 67.61: the litter's regional density at a place (× its kinds' amounts): 0 bare, 0.25 … 0.75 sparse to medium, 1.25 dense.
 * `at` the place in units of the region (a planet's direction × its radius ÷ (regionPeople × person)); deterministic in
 * `seed`. The biome and the material set where the bands fall; the climate shifts them (wet: denser; dry and windy: barer).
 */
export function litterDensity(at: Vec3, seed: number, biome: LitterBiome, material: GroundMaterial, climate: LitterClimate): number {
  const raw = 0.6 * valueNoise(at.x, at.y, at.z, seed) + 0.4 * valueNoise(at.x * 2.3 + 17, at.y * 2.3 - 5, at.z * 2.3 + 9, seed + 1);
  const n = Math.min(1, Math.max(0, (raw - 0.5) * 2.1 + 0.5));   // (two octaves bunch round a half: stretched, so the bands' tails are reached)
  const b = BANDS[biome ?? 'open'] ?? BANDS.open;
  const bare = Math.min(0.95, b.bare + (BARER[material] ?? 0) + 0.3 * climate.dry + 0.15 * climate.windy - 0.1 * climate.wet);
  const dense = Math.min(0.99, Math.max(bare + 0.05, b.dense + 0.1 * climate.dry - 0.15 * climate.wet));
  if (n < bare) return 0;
  if (n > dense) return 1.25;
  return 0.25 + 0.5 * (n - bare) / (dense - bare);
}

/** The ground as the litter sees it (world units): its up at a point, the ground point along up through `p`, its biome and material there, and whether litter may lie there. */
export interface LitterGround {
  person: number;
  up(p: Vec3): Vec3;
  surface(p: Vec3): Vec3 | null;
  biome(p: Vec3): LitterBiome;
  material(p: Vec3): GroundMaterial;
  blocked(p: Vec3): boolean;
  /** 67.61: the regional density there (× the kinds' amounts; absent: 1) */
  density?(p: Vec3): number;
}

/** How much of each kind lies on a place (0 … 1 of its slots) and their tints, by its biome and material. */
interface Mix { amount: Partial<Record<LitterKind, number>>; tint: Partial<Record<LitterKind, [string, string]>> }
const BY_MATERIAL: Partial<Record<GroundMaterial, Mix>> = {
  rock: { amount: { pebbles: 1, debris: 0.8, blades: 0.15 }, tint: { pebbles: ['#7d786e', '#9a948a'], debris: ['#6c675e', '#8a857a'], blades: ['#7b8a52', '#94a060'] } },
  sand: { amount: { pebbles: 0.5, debris: 0.25, blades: 0.2, twigs: 0.2 }, tint: { pebbles: ['#b8a888', '#d2c4a2'], debris: ['#a89878', '#c4b490'], blades: ['#b4a868', '#cabe80'], twigs: ['#8a7a5a', '#a8946e'] } },
  snow: { amount: { pebbles: 0.25, twigs: 0.2, debris: 0.15 }, tint: { pebbles: ['#7a7a80', '#9a9aa2'], twigs: ['#4a3e34', '#5e5044'], debris: ['#c8d2dc', '#e4ecf2'] } },
  ice: { amount: { debris: 0.4, pebbles: 0.15 }, tint: { debris: ['#b8d0dc', '#dceaf0'], pebbles: ['#6e7a84', '#8e9aa4'] } },
  mud: { amount: { leaves: 0.6, twigs: 0.6, blades: 0.4, pebbles: 0.2 }, tint: { leaves: ['#4a3e28', '#5e4e30'], twigs: ['#3a2e22', '#4a3c2c'], blades: ['#4e5e30', '#62703a'], pebbles: ['#4e4638', '#625a48'] } },
  ash: { amount: { debris: 0.9, twigs: 0.5, pebbles: 0.4 }, tint: { debris: ['#2a2826', '#46423e'], twigs: ['#1e1c1a', '#34302c'], pebbles: ['#5a5652', '#76726c'] } },
  salt: { amount: { pebbles: 0.7, debris: 0.5 }, tint: { pebbles: ['#d8d4c8', '#f0ece4'], debris: ['#e0dcd2', '#f4f2ec'] } },
};
/** On soil, by the biome over it (open ground: a little grass and stone). */
const BY_BIOME: Record<string, Mix> = {
  open: { amount: { blades: 0.55, pebbles: 0.35, twigs: 0.08 }, tint: { blades: ['#6c7e44', '#8a9a56'], pebbles: ['#7a7266', '#948c80'], twigs: ['#5e4c38', '#76624a'] } },
  broadleaf: { amount: { leaves: 1, twigs: 0.6, blades: 0.4, pebbles: 0.15 }, tint: { leaves: ['#7a5a2e', '#a0783a'], twigs: ['#4e3e2c', '#665238'], blades: ['#5e7a3e', '#7a9450'], pebbles: ['#6e665a', '#8a8274'] } },
  birchGlade: { amount: { leaves: 0.8, blades: 0.6, twigs: 0.4 }, tint: { leaves: ['#b0943e', '#cab060'], blades: ['#6e8a46', '#8aa25a'], twigs: ['#d2ccbe', '#e6e0d2'] } },
  firStand: { amount: { needles: 1, twigs: 0.6, pebbles: 0.3, blades: 0.15 }, tint: { needles: ['#6e5236', '#8a6a44'], twigs: ['#4a3a2c', '#5e4a36'], pebbles: ['#6a6458', '#827c70'], blades: ['#4e6a40', '#62804e'] } },
  hollowWood: { amount: { needles: 0.7, leaves: 0.5, twigs: 0.7 }, tint: { needles: ['#5e4630', '#76583c'], leaves: ['#5e4a2a', '#7a6036'], twigs: ['#3e3226', '#524234'] } },
  heath: { amount: { blades: 0.8, pebbles: 0.5, twigs: 0.2 }, tint: { blades: ['#8a7e48', '#a0965a'], pebbles: ['#76706a', '#8e8880'], twigs: ['#5a4a38', '#6e5c46'] } },
  savanna: { amount: { blades: 0.9, pebbles: 0.3, twigs: 0.25 }, tint: { blades: ['#b09a58', '#c8b46e'], pebbles: ['#8a7a64', '#a4947c'], twigs: ['#6e5a40', '#86704e'] } },
  scrub: { amount: { blades: 0.6, twigs: 0.6, pebbles: 0.4 }, tint: { blades: ['#9a8e56', '#b2a66a'], twigs: ['#5e4c38', '#76624a'], pebbles: ['#7e766a', '#968e80'] } },
  ashBarrens: { amount: { debris: 0.8, pebbles: 0.5, twigs: 0.3 }, tint: { debris: ['#3a3632', '#56514c'], pebbles: ['#605a54', '#7a746c'], twigs: ['#2a2622', '#3e3830'] } },
  burn: { amount: { debris: 0.7, twigs: 0.8, leaves: 0.2 }, tint: { debris: ['#2a2624', '#423c38'], twigs: ['#1e1a18', '#322c28'], leaves: ['#4a3a26', '#5e4a30'] } },
  lanternGrove: { amount: { leaves: 0.7, blades: 0.6, pebbles: 0.3 }, tint: { leaves: ['#8a6a3e', '#a8844e'], blades: ['#5e7e52', '#78986a'], pebbles: ['#6e6a62', '#88847a'] } },
  /** 67.61 (1): the jungle floor — leaf litter thick, fallen twigs, broad blades */
  jungle: { amount: { leaves: 1, twigs: 0.8, blades: 0.7 }, tint: { leaves: ['#5e4a26', '#7a6232'], twigs: ['#3a2e20', '#4e3e2a'], blades: ['#3e6a32', '#548444'] } },
};
function mixAt(material: GroundMaterial, biome: LitterBiome): Mix {
  return material !== 'soil' && BY_MATERIAL[material] ? BY_MATERIAL[material]! : BY_BIOME[biome ?? 'open'] ?? BY_BIOME.open;
}

/** Each kind's piece (person units, lying on +y, facing +z): a few triangles. */
function pieceGeometry(kind: LitterKind): THREE.BufferGeometry {
  const pos: number[] = [];
  const tri = (a: number[], b: number[], c: number[]) => pos.push(...a, ...b, ...c);
  if (kind === 'blades') {
    // a tuft: five blades leaning out from the middle
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2, lean = 0.12, h = 0.28 + 0.08 * (i % 2), w = 0.035;
      const cx = Math.cos(a), cz = Math.sin(a);
      tri([-w * cz, 0, w * cx], [w * cz, 0, -w * cx], [cx * lean, h, cz * lean]);
    }
  } else if (kind === 'leaves') {
    // a fallen leaf: a flat diamond, a little cupped
    tri([0, 0.005, -0.09], [0.05, 0.012, 0], [0, 0.005, 0.09]); tri([0, 0.005, -0.09], [0, 0.005, 0.09], [-0.05, 0.012, 0]);
  } else if (kind === 'needles') {
    // a scatter of needles and a cone: a flat irregular patch and a tiny pyramid
    tri([-0.14, 0.004, -0.08], [0.15, 0.004, -0.04], [0.02, 0.004, 0.14]);
    tri([0.06, 0, 0], [0.1, 0.07, 0.03], [0.08, 0, 0.07]); tri([0.06, 0, 0], [0.12, 0, 0.02], [0.1, 0.07, 0.03]);
  } else if (kind === 'twigs') {
    // a twig and its fork: two thin crossed strips
    for (const [dx, dz, len] of [[1, 0, 0.42], [0.6, 0.8, 0.18]] as const) {
      const w = 0.012, ex = dx * len, ez = dz * len, nx = -dz * w, nz = dx * w;
      tri([nx, 0.012, nz], [ex + nx, 0.012, ez + nz], [ex - nx, 0.012, ez - nz]); tri([nx, 0.012, nz], [ex - nx, 0.012, ez - nz], [-nx, 0.012, -nz]);
      tri([0, 0.024, 0], [ex, 0.024, ez], [ex, 0, ez]); tri([0, 0.024, 0], [ex, 0, ez], [0, 0, 0]);
    }
  } else if (kind === 'pebbles') {
    // a pebble: a squashed octahedron
    const r = 0.06, h = 0.04, v = [[r, 0, 0], [0, 0, r * 0.8], [-r, 0, 0], [0, 0, -r * 0.8]];
    for (let i = 0; i < 4; i++) { const a = v[i], b = v[(i + 1) % 4]; tri(a, [0, h, 0], b); tri(b, [0, -h * 0.3, 0], a); }
  } else {
    // debris: a shard — a tilted flat wedge
    tri([-0.09, 0, -0.06], [0.1, 0.03, -0.02], [0.0, 0.07, 0.08]); tri([-0.09, 0, -0.06], [0.0, 0.07, 0.08], [-0.03, 0, 0.09]); tri([0.1, 0.03, -0.02], [0.08, 0, 0.06], [0.0, 0.07, 0.08]);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

/**
 * A material that shrinks each instance to nothing over the fade band (by its distance from `focus`, a uniform); 69.76: and
 * grows it in over `grow.y` seconds from its birth (the `aLitterBorn` attribute, against the field's clock `grow.x`).
 */
export function fadingMaterial(focus: { value: THREE.Vector3 }, band: { value: THREE.Vector2 }, grow: { value: THREE.Vector2 } = { value: new THREE.Vector2(0, 0) }): THREE.MeshLambertMaterial {
  const m = markShared(new THREE.MeshLambertMaterial({ color: 0xffffff, side: THREE.DoubleSide }));
  m.onBeforeCompile = shader => {
    shader.uniforms.uLitterFocus = focus;
    shader.uniforms.uLitterBand = band;
    shader.uniforms.uLitterGrow = grow;
    shader.vertexShader = 'uniform vec3 uLitterFocus;\nuniform vec2 uLitterBand;\nuniform vec2 uLitterGrow;\nattribute float aLitterBorn;\n' + shader.vertexShader.replace('#include <begin_vertex>',
      `#include <begin_vertex>
      #ifdef USE_INSTANCING
        vec3 litterAt = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
        float litterFade = 1.0 - smoothstep(uLitterBand.x, uLitterBand.y, distance(litterAt, uLitterFocus));
        float litterGrow = uLitterGrow.y > 0.0 ? smoothstep(0.0, uLitterGrow.y, uLitterGrow.x - aLitterBorn) : 1.0;
        transformed *= litterFade * litterGrow;
      #endif`);
  };
  m.customProgramCacheKey = () => 'groundLitter';
  return m;
}

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _s = new THREE.Vector3(), _p = new THREE.Vector3(), _u = new THREE.Vector3(), _y = new THREE.Vector3(0, 1, 0), _c = new THREE.Color(), _c2 = new THREE.Color();
const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);

/** A small integer hash → 0 … 1, deterministic in its inputs (the cube's key and the piece's index). */
function hash(a: number, b: number, c: number, d: number): number {
  let h = Math.imul(a, 0x27d4eb2d) ^ Math.imul(b, 0x165667b1) ^ Math.imul(c, 0x9e3779b1) ^ Math.imul(d + 0x632be5ab, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d); h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

/** The field: create it with its ground and scene; `update(focus)` each frame with the camera's ground point (world). */
export class LitterField {
  readonly meshes: Record<LitterKind, THREE.InstancedMesh>;
  /** the live cubes: key → their slot block */
  private readonly cells = new Map<string, number>();
  private readonly free: number[] = [];
  private readonly focus = { value: new THREE.Vector3() };
  private readonly band = { value: new THREE.Vector2() };
  private readonly size: number;
  readonly capacity: number;
  /** what the last update did (for the sheet's readout): cubes made, ms spent, cubes live, pieces placed */
  last = { made: 0, ms: 0, live: 0, pieces: 0, walkMs: 0 };
  private dirty = new Set<LitterKind>();
  /** the wanted cubes as last walked, and where from (re-walked when the focus moves a third of a cube, or cubes are pending) */
  private want = new Map<string, [number, number, number]>();
  /** the keep's set (the reach × keepFactor): a cube outside it is dropped */
  private keepSet = new Set<string>();
  private wantAt: string | null = null;
  private pending = 0;
  /** 69.76: the field's clock (seconds, the sum of the updates' dt), its grow-in uniform (clock, growS), the focus last frame and its smoothed velocity (world units a second) */
  private clock = 0;
  private readonly grow = { value: new THREE.Vector2(0, LITTER.growS) };
  private lastFocus: THREE.Vector3 | null = null;
  private readonly velocity = new THREE.Vector3();

  /** × every place's amounts (the Lab's dial; 0 makes nothing) */
  scale = 1;

  constructor(private readonly scene: THREE.Object3D, private readonly ground: LitterGround) {
    const person = ground.person, cell = LITTER.cellPeople * person, reach = LITTER.reachPeople * person;
    this.size = cell;
    // the most cubes the ground can cross in the keep's reach: the disc's area over a cube's face, with room for slopes
    const keep = reach * LITTER.keepFactor;
    this.capacity = Math.ceil((Math.PI * keep * keep) / (cell * cell) * 1.6) + 16;
    for (let i = this.capacity - 1; i >= 0; i--) this.free.push(i);
    this.band.value.set(reach * LITTER.fadeFrom, reach);
    this.meshes = {} as Record<LitterKind, THREE.InstancedMesh>;
    for (const kind of LITTER_KINDS) {
      const geo = pieceGeometry(kind), k = (person / 1.8) * LITTER.pieceScale; geo.scale(k, k, k);   // (person units → world: a person is 1.8 person units)
      const n = this.capacity * LITTER.slots[kind];
      geo.setAttribute('aLitterBorn', new THREE.InstancedBufferAttribute(new Float32Array(n).fill(-1e9), 1).setUsage(THREE.DynamicDrawUsage));
      const mesh = new THREE.InstancedMesh(geo, fadingMaterial(this.focus, this.band, this.grow), n);
      mesh.name = `litter:${kind}`;
      mesh.frustumCulled = false;
      mesh.userData.noBake = true;
      for (let i = 0; i < n; i++) { mesh.setMatrixAt(i, ZERO); mesh.setColorAt(i, _c.set(1, 1, 1)); }
      scene.add(mesh);
      this.meshes[kind] = mesh;
    }
  }

  /**
   * Bring the field to the camera's ground point `focus` (world) after `dt` seconds: drop the cubes past the keep, make the
   * missing ones in reach of the (motion-led) walk, nearest first — `cellsPerFrame`, more at speed (69.76).
   */
  update(focus: Vec3, dt = 1 / 60): void {
    const t0 = performance.now(), g = this.ground, person = g.person, reach = LITTER.reachPeople * person, cell = this.size;
    this.focus.value.set(focus.x, focus.y, focus.z);
    // 69.76: the clock, and the focus's motion (smoothed over a few frames; a jump past the reach — a new planet, a respawn — is no motion)
    this.clock += dt; this.grow.value.x = this.clock;
    if (this.lastFocus && dt > 0) {
      const vx = (focus.x - this.lastFocus.x) / dt, vy = (focus.y - this.lastFocus.y) / dt, vz = (focus.z - this.lastFocus.z) / dt;
      if (Math.hypot(vx, vy, vz) * dt > reach) this.velocity.set(0, 0, 0);
      else { const a = Math.min(1, dt / 0.15); this.velocity.set(this.velocity.x + (vx - this.velocity.x) * a, this.velocity.y + (vy - this.velocity.y) * a, this.velocity.z + (vz - this.velocity.z) * a); }
    }
    (this.lastFocus ??= new THREE.Vector3()).set(focus.x, focus.y, focus.z);
    const keep = reach * LITTER.keepFactor, speed = this.velocity.length();
    // the walk's centre: led along the motion, never past the keep's margin (what it makes stays kept)
    const lead = Math.min(speed * LITTER.leadS, keep - reach), ls = speed > 1e-9 ? lead / speed : 0;
    let at: Vec3 = { x: focus.x + this.velocity.x * ls, y: focus.y + this.velocity.y * ls, z: focus.z + this.velocity.z * ls };
    const led = at;
    // the cubes the ground crosses in reach: walk a tangent grid round `at` (a step under a cube), each sample's ground point's cube
    // the walk is anchored to the focus's cube (its centre): the same while the focus stays in one cube, re-walked when it
    // enters another — not a grid riding the camera (its rim flipped cubes in and out every walk)
    const fk = `${Math.floor(at.x / cell)},${Math.floor(at.y / cell)},${Math.floor(at.z / cell)}`;
    const moved = this.wantAt !== fk;
    if (!moved && this.pending === 0) { this.last = { made: 0, ms: performance.now() - t0, live: this.cells.size, pieces: 0, walkMs: 0 }; return; }
    if (moved) at = { x: (Math.floor(at.x / cell) + 0.5) * cell, y: (Math.floor(at.y / cell) + 0.5) * cell, z: (Math.floor(at.z / cell) + 0.5) * cell };
    const up = g.up(at), t1 = new THREE.Vector3(up.y, -up.x, 0);
    if (t1.lengthSq() < 1e-6) t1.set(0, up.z, -up.y);
    t1.normalize();
    const t2 = new THREE.Vector3().crossVectors(new THREE.Vector3(up.x, up.y, up.z), t1);
    const want = moved ? new Map<string, [number, number, number]>() : this.want, keepSet = moved ? new Set<string>() : this.keepSet, steps = Math.ceil(keep / (cell * 0.7));
    if (moved) for (let i = -steps; i <= steps; i++) for (let j = -steps; j <= steps; j++) {
      const di = i * cell * 0.7, dj = j * cell * 0.7, r2 = di * di + dj * dj;
      if (r2 > keep * keep) continue;
      const s = g.surface({ x: at.x + t1.x * di + t2.x * dj, y: at.y + t1.y * di + t2.y * dj, z: at.z + t1.z * di + t2.z * dj });
      if (!s) continue;
      const k: [number, number, number] = [Math.floor(s.x / cell), Math.floor(s.y / cell), Math.floor(s.z / cell)], key = `${k[0]},${k[1]},${k[2]}`;
      keepSet.add(key);
      if (r2 <= reach * reach) want.set(key, k);
    }
    if (moved) { this.want = want; this.keepSet = keepSet; this.wantAt = fk; }
    const walkMs = performance.now() - t0;
    // drop what is past the keep — 69.76: by its distance from the focus, not by whether this walk's samples found it (a cube
    // the ground only clips was missed by one walk and found by the next: dropped and made again beside the camera, its pieces
    // blinking out and in); then make the nearest of what is missing in reach, up to the frame's budget
    const far = keep + cell * 0.87;   // (a cube's half-diagonal)
    for (const [key, slot] of this.cells) {
      const [i, j, k] = key.split(',').map(Number);
      const dx = (i + 0.5) * cell - focus.x, dy = (j + 0.5) * cell - focus.y, dz = (k + 0.5) * cell - focus.z;
      if (dx * dx + dy * dy + dz * dz > far * far) { this.clear(slot); this.cells.delete(key); this.free.push(slot); }
    }
    const missing = [...want].filter(([key]) => !this.cells.has(key));
    missing.sort((a, b) => dist2(a[1], cell, led) - dist2(b[1], cell, led));
    // 69.76: the cubes a frame — the reach's front sweeps 2 reach × speed × dt of ground a frame, a cube a cell² of it
    const sweep = (2 * reach * speed * dt) / (cell * cell);
    const cap = Math.min(LITTER.maxCellsPerFrame, Math.max(LITTER.cellsPerFrame, Math.ceil(LITTER.rateMargin * sweep)));
    let made = 0, pieces = 0;
    const tm = performance.now();
    for (const [key, k] of missing) {
      if (made >= cap || !this.free.length || (made >= LITTER.cellsPerFrame && performance.now() - tm > LITTER.budgetMs)) break;
      const slot = this.free.pop()!;
      pieces += this.fill(slot, k);
      this.cells.set(key, slot); made++;
    }
    this.pending = missing.length - made;
    for (const kind of this.dirty) {
      const m = this.meshes[kind];
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
    this.dirty.clear();
    this.last = { made, ms: performance.now() - t0, live: this.cells.size, pieces, walkMs };
  }

  /**
   * 69.41 (DECISIONS 254 (2), the owner: "litter to snap ~1 person higher so it's floating in the air in the region surrounding
   * a dropship crash"): the ground moved within `radius` of `at` (world) — a crater, a mound. A cube's pieces were laid on the
   * ground of the moment it was made and never moved, so the ones made before a crater hung where the ground had been. The
   * cubes there are laid again on the ground as it is now, in place and whole (69.76: not dropped for the walk to make again
   * and grow in — a blink round every crater); one the ground has left is dropped. The walk is redone, since a lowered
   * ground can fall into the cube below. The number of cubes re-laid or dropped.
   */
  regroundNear(at: Vec3, radius: number): number {
    const cell = this.size, near = radius + cell * 0.87;   // (a cube's half-diagonal)
    let n = 0;
    for (const [key, slot] of this.cells) {
      const [i, j, k] = key.split(',').map(Number);
      const dx = (i + 0.5) * cell - at.x, dy = (j + 0.5) * cell - at.y, dz = (k + 0.5) * cell - at.z;
      if (dx * dx + dy * dy + dz * dz > near * near) continue;
      if (!this.fill(slot, [i, j, k], false)) { this.clear(slot); this.cells.delete(key); this.free.push(slot); }
      n++;
    }
    if (n) this.wantAt = null;   // (the walk redone on the next update)
    return n;
  }

  /** Lay a cube's pieces into its slot block: per kind, by the place's mix, each a hash of the cube's key and its index; `grow` (69.76): grown in from now, else as it was (re-laid). */
  private fill(slot: number, k: [number, number, number], grow = true): number {
    const g = this.ground, cell = this.size;
    const mid = { x: (k[0] + 0.5) * cell, y: (k[1] + 0.5) * cell, z: (k[2] + 0.5) * cell };
    const anchor = g.surface(mid) ?? mid, mix = mixAt(g.material(anchor), g.biome(anchor));
    const dens = this.scale * (g.density ? g.density(anchor) : 1);   // (67.61: the region's density × the dial)
    let placed = 0;
    for (const [ki, kind] of LITTER_KINDS.entries()) {
      const slots = LITTER.slots[kind], want = Math.min(slots, Math.round(slots * (mix.amount[kind] ?? 0) * dens)), mesh = this.meshes[kind], tint = mix.tint[kind];
      const born = mesh.geometry.getAttribute('aLitterBorn') as THREE.InstancedBufferAttribute;
      for (let i = 0; i < slots; i++) {
        const idx = slot * slots + i;
        if (grow) (born.array as Float32Array)[idx] = this.clock;   // 69.76: grown in from now (re-laid: as it was)
        let ok = false;
        if (i < want && tint) {
          // a point in the cube, its ground point along up; kept when that falls in this cube (each ground point in one cube)
          const h = (n: number) => hash(k[0], k[1], k[2], ki * 64 + i * 4 + n);
          const s = g.surface({ x: (k[0] + h(0)) * cell, y: (k[1] + h(1)) * cell, z: (k[2] + h(2)) * cell });
          if (s && Math.floor(s.x / cell) === k[0] && Math.floor(s.y / cell) === k[1] && Math.floor(s.z / cell) === k[2] && !g.blocked(s)) {
            const up = g.up(s);
            _q.setFromUnitVectors(_y, _u.set(up.x, up.y, up.z));
            _q2.setFromAxisAngle(_y, h(3) * Math.PI * 2);
            const sc = 0.7 + 0.7 * hash(k[0], k[1], k[2], ki * 64 + i * 4 + 7);
            _m.compose(_p.set(s.x, s.y, s.z), _q.multiply(_q2), _s.set(sc, sc, sc));
            mesh.setMatrixAt(idx, _m);
            mesh.setColorAt(idx, _c.set(tint[0]).lerp(_c2.set(tint[1]), hash(k[0], k[1], k[2], ki * 64 + i * 4 + 9)));
            ok = true; placed++;
          }
        }
        if (!ok) mesh.setMatrixAt(idx, ZERO);
      }
      if (grow) born.needsUpdate = true;
      this.dirty.add(kind);
    }
    return placed;
  }

  private clear(slot: number): void {
    for (const kind of LITTER_KINDS) { const slots = LITTER.slots[kind]; for (let i = 0; i < slots; i++) this.meshes[kind].setMatrixAt(slot * slots + i, ZERO); this.dirty.add(kind); }
  }

  /** Its draws (one a kind) and the triangles at full occupancy. */
  cost(): { draws: number; triangles: number; instances: number } {
    let triangles = 0, instances = 0;
    for (const kind of LITTER_KINDS) { const m = this.meshes[kind]; instances += m.count; triangles += m.count * (m.geometry.attributes.position.count / 3); }
    return { draws: LITTER_KINDS.length, triangles, instances };
  }

  dispose(): void {
    for (const kind of LITTER_KINDS) { const m = this.meshes[kind]; this.scene.remove(m); m.geometry.dispose(); m.dispose(); }
  }
}
function dist2(k: [number, number, number], cell: number, at: Vec3): number {
  const x = (k[0] + 0.5) * cell - at.x, y = (k[1] + 0.5) * cell - at.y, z = (k[2] + 0.5) * cell - at.z;
  return x * x + y * y + z * z;
}
